import React from 'react'
import { useTheme } from '@emotion/react';
import { CircularProgress, Grid, IconButton, Paper, Typography } from '@mui/material'
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import { FormikProvider, useFormik } from 'formik';
import * as Yup from "yup";
import { Primary } from 'ui-components/Buttons';
import CustomInputField from 'ui-components/formik/CustomInputField';
import ModalComponent from 'ui-components/ModalComponent';
import { UpdateUserEmail } from 'api/email_bounces';
import { useToast } from 'context/ToastContext';

// Admin fix for a bounced (or wrong) email on a startup or investor. Saving
// moves the account's login email and clears the bounce flag for that address.
const UpdateEmailPopup = ({ open, handleClose, entity, row, onUpdated = () => { } }) => {
    const theme = useTheme();
    const { addToast } = useToast();
    const { mutateAsync: updateEmailAsync, isLoading } = UpdateUserEmail();
    const bounce = row?.email_bounce;

    const formik = useFormik({
        initialValues: { email: row?.email || "" },
        validationSchema: Yup.object({
            email: Yup.string().trim().email("Please enter a valid email").required("Please enter email"),
        }),
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                await updateEmailAsync({ entity, id: row?._id, email: values.email.trim() });
                addToast("Email updated", "success");
                onUpdated();
                handleClose();
            } catch (error) {
                addToast(error?.response?.data?.message || "Error: Something went wrong.", "error");
            }
        },
    });

    return (
        <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: 520, width: "100%" }}>
            <Paper sx={{ background: theme.palette.customColors.whiteBG, borderRadius: "8px" }}>
                <IconButton sx={{ position: "absolute", top: 20, right: 20, padding: 0 }} onClick={handleClose}>
                    <CloseIcon width={40} height={40} />
                </IconButton>
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid display={"grid"} container padding={"1.5rem"} gap={"1.25rem"}>
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, lineHeight: "28px", color: theme.palette.customColors.g300 }}>
                                    Update Email
                                </Typography>
                                <Typography sx={{ fontSize: "0.875rem", color: theme.palette.customColors.g200, marginTop: 0.5 }}>
                                    This also changes the email they sign in with.
                                </Typography>
                            </Grid>
                            {bounce && (
                                <Grid item xs={12} sx={{
                                    padding: "0.75rem 1rem",
                                    borderRadius: "8px",
                                    backgroundColor: theme.palette.customColors.r50,
                                    border: `1px solid ${theme.palette.customColors.r75}`,
                                }}>
                                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: theme.palette.customColors.r500 }}>
                                        Emails to {row?.email} are not being delivered
                                    </Typography>
                                    {bounce?.reason && (
                                        <Typography sx={{ fontSize: "0.8125rem", color: theme.palette.customColors.r400, marginTop: 0.5, wordBreak: "break-word" }}>
                                            {bounce.reason}
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontSize: "0.8125rem", color: theme.palette.customColors.r400, marginTop: 0.5 }}>
                                        Enter a working address, or save the same one if you've confirmed it now receives mail.
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <CustomInputField
                                    name="email"
                                    label={"Email"}
                                    placeholder={"Enter email"}
                                    value={formik?.values?.email}
                                    sx={{ marginBottom: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Primary
                                    type="submit"
                                    fullWidth
                                    sx={{ padding: "0.75rem 1.25rem", fontSize: "1rem", height: '52px' }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? <CircularProgress sx={{ color: "white", marginInline: 'auto' }} size={'1.2rem'} /> : "Save"}
                                </Primary>
                            </Grid>
                        </Grid>
                    </form>
                </FormikProvider>
            </Paper>
        </ModalComponent>
    )
}

export default UpdateEmailPopup
